<!--
* A price field that includes a currency dropdown and a formatted input field
*
* @param {number} selectedCurrency - Index of selected currency in currencies
* @param {string} inputValue - Value of input field
* @param {Object | Object[]} currencies - currencies to show in select dropdown
* @param {string} selectDisplayKey - Key in currencies objects to get
*                                    display name for select options
-->

<!--
 * update:inputValue event.
 * Emitted when inputValue is changed.
 * Can be used to update inputValue prop on implementation by using .sync
 * @see https://vuejs.org/v2/guide/components-custom-events.html#sync-Modifier
 *
 * @event MsInputWithPrefix#update:inputValue
 -->
<template>
  <div class="ms-price-input-container">
    <AppSelect
      v-model="selectVal"
      class="prefix-select"
      :displayKey="selectDisplayKey"
      :options="currencies"
      name="currency" />
    <input
      type="number"
      class="input"
      :class="{'is-danger': !isValid}"
      v-model="inputVal"
      @input="updateInputValue"
      autocomplete="off"/>
  </div>
</template>

<script>
  import AppSelect from '@/components/app-select'
  const isPriceValidRegExp = new RegExp(/^(\d)+?(\.\d{0,2})?$/)

  export default {
    name: 'MsInputWithPrefix',
    components: {
      AppSelect
    },

    data: function () {
      return {
        inputVal: this.inputValue ? this.inputValue : null,
        isValid: true
      }
    },

    props: {
      selectedCurrency: {
        type: Number,
        required: true
      },

      inputValue: {
        type: Number,
        required: true
      },

      currencies: {
        type: [Array, Object],
        required: true
      },

      selectDisplayKey: {
        type: String,
        required: true
      }
    },

    computed: {
      selectVal: {
        /**
         * Gets current selected currency
         */
        get () {
          return this.selectedCurrency
        },

        /**
         * Emits update event for prop select
         * @see https://vuejs.org/v2/guide/components-custom-events.html#sync-Modifier
         *
         * @param {Object} value - Object of selected item
         */
        set (value) {
          this.$emit('update:selectedCurrency', value)
        }
      }
    },

    methods: {
      /**
       * Emits update event for prop input
       * If the value is invalid, null is sent
       * @see https://vuejs.org/v2/guide/components-custom-events.html#sync-Modifier
       *
       * @param {Object} event - Input event
       */
      updateInputValue: function (event) {
        let value = event.target.value
        this.isValid = this.isValidPrice(value)
        if (this.isValid) {
          this.$emit('update:inputValue', value)
        } else {
          this.$emit('update:inputValue', null)
        }
      },

      /**
       * Validates price using regex
       * RegExp allows following types of values:
       * 1 ; 0.9 ; 12. ; 12.12 ;
       *
       * @param {Number} value - price
       */
      isValidPrice: function (value) {
        let isValid = isPriceValidRegExp.test(value)
        return isValid
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "../assets/sass/shared.scss";

  .ms-price-input-container {
    display: flex;
    background: $secondary-brand-color;
    border: $default-border-style;
  }

  .input,
  .prefix-select {
    border: none;
    width: 50%;
    height: auto;
    line-height: 1em;
  }

  .is-danger {
    border: inherit;
  }

  .prefix-select ::v-deep .select {
    height: auto;
  }

  .prefix-select ::v-deep select {
    border: none;
    width: 100%;
    height: auto;
    border-right: $default-border-style
  }
</style>
